import { useEffect, useCallback } from 'react';
import { X, Mic, Clock } from 'lucide-react';
import useVoiceDumpStore from '../../stores/useVoiceDumpStore';
import RecordStep from './RecordStep';
import ProcessingStep from './ProcessingStep';
import ReviewStep from './ReviewStep';
import DoneStep from './DoneStep';
import HistoryStep from './HistoryStep';
import CreditPrompt from './CreditPrompt';
import { getModeFromPath } from '../../utils/voiceDumpModes';
import './VoiceDumpWizard.css';

const STEP_INDEX = {
  recording: 0,
  processing: 1,
  review: 2,
  executing: 2,
  done: 3,
  history: -1, // Not part of the main flow
};

const STEP_LABELS = ['Record', 'Process', 'Review', 'Done'];

export default function VoiceDumpWizard() {
  const isOpen = useVoiceDumpStore(s => s.isOpen);
  const step = useVoiceDumpStore(s => s.step);
  const error = useVoiceDumpStore(s => s.error);
  const closeWizard = useVoiceDumpStore(s => s.closeWizard);
  const showHistory = useVoiceDumpStore(s => s.showHistory);
  const dumpHistory = useVoiceDumpStore(s => s.dumpHistory);

  const openWizard = useVoiceDumpStore(s => s.openWizard);

  // Close on Escape
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') closeWizard();
  }, [closeWizard]);

  // Global shortcut: Cmd+Shift+V / Ctrl+Shift+V
  useEffect(() => {
    const handleGlobalShortcut = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === 'V') {
        e.preventDefault();
        if (!isOpen) {
          openWizard(getModeFromPath(window.location.pathname));
        }
      }
    };
    document.addEventListener('keydown', handleGlobalShortcut);
    return () => document.removeEventListener('keydown', handleGlobalShortcut);
  }, [isOpen, openWizard]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  // API key or Credits error state
  if (error === 'NO_CREDITS_OR_KEY' || error === 'INSUFFICIENT_CREDITS' || error === 'NO_API_KEY') {
    return (
      <div className="vd-overlay" onClick={closeWizard}>
        <div className="vd-modal" onClick={e => e.stopPropagation()} role="dialog" aria-label="Voice Dump — AI Credits/Key Required">
          <div className="vd-header">
            <h2 className="vd-header__title"><Mic size={20} /> Voice Dump</h2>
            <button className="btn btn-ghost btn-sm btn-icon vd-header__close" onClick={closeWizard} aria-label="Close"><X size={20} /></button>
          </div>
          <div className="vd-body">
            <CreditPrompt errorType={error} />
          </div>
        </div>
      </div>
    );
  }

  const currentStepIndex = STEP_INDEX[step] ?? 0;

  const renderStep = () => {
    switch (step) {
      case 'recording': return <RecordStep />;
      case 'processing':
      case 'executing': return <ProcessingStep isExecuting={step === 'executing'} />;
      case 'review': return <ReviewStep />;
      case 'done': return <DoneStep />;
      case 'history': return <HistoryStep />;
      default: return <RecordStep />;
    }
  };

  return (
    <div className="vd-overlay" onClick={closeWizard}>
      <div className="vd-modal" onClick={e => e.stopPropagation()} role="dialog" aria-label="Voice Dump Wizard">
        {/* Header */}
        <div className="vd-header">
          <h2 className="vd-header__title"><Mic size={20} /> Voice Dump</h2>
          <div className="vd-header__actions">
            {step === 'recording' && dumpHistory.length > 0 && (
              <button className="btn btn-ghost btn-sm btn-icon vd-header__history" onClick={showHistory} aria-label="View dump history" title="History">
                <Clock size={18} />
              </button>
            )}
            <button className="btn btn-ghost btn-sm btn-icon vd-header__close" onClick={closeWizard} aria-label="Close">
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Step indicators — hide for history view */}
        {currentStepIndex >= 0 && (
          <div className="vd-steps" aria-label={`Step ${currentStepIndex + 1} of ${STEP_LABELS.length}: ${STEP_LABELS[currentStepIndex]}`}>
            {STEP_LABELS.map((label, i) => (
              <div
                key={label}
                className={`vd-step-dot ${i === currentStepIndex ? 'vd-step-dot--active' : ''} ${i < currentStepIndex ? 'vd-step-dot--completed' : ''}`}
                title={label}
              />
            ))}
          </div>
        )}

        {/* Body */}
        <div className="vd-body">
          {renderStep()}
        </div>
      </div>
    </div>
  );
}
